/**
 * ProgressSummary - Summary Cards Section
 * Displays summary statistics and quick views of task progress
 */

import React from 'react';
import { Target, Loader, Calendar, CheckCircle2 } from 'lucide-react';
import { getNextAction, getDaysUntilDue, formatDueDate } from '../../utils/taskUtils';

const SummaryCard = ({ icon: Icon, title, value, subtitle, color = 'indigo', onClick, className = '' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900'
  };

  const iconColorClasses = {
    indigo: 'text-indigo-600',
    green: 'text-green-600',
    orange: 'text-orange-600',
    purple: 'text-purple-600'
  };

  return (
    <div
      onClick={onClick}
      className={`${colorClasses[color]} border-2 rounded-lg p-4 ${
        onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className={`${iconColorClasses[color]} flex-shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide opacity-75 mb-1">
            {title}
          </p>
          <p className="text-2xl font-bold mb-1">
            {value}
          </p>
          {subtitle && (
            <p className="text-sm opacity-75">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressSummary = ({
  tasks = [],
  taskSubtasksMap = {},
  onNextActionClick,
  onInProgressClick,
  onThisWeekClick,
  className = ''
}) => {
  // Calculate next action
  const nextAction = getNextAction(tasks, taskSubtasksMap);

  // Calculate in-progress count
  const inProgressCount = tasks.filter(
    task => task.status === 'in_progress' ||
    (task.status === 'planned' && task.completed_step_count > 0)
  ).length;

  // Calculate this week count
  const thisWeekEnd = new Date();
  thisWeekEnd.setDate(thisWeekEnd.getDate() + 7);
  const thisWeekCount = tasks.filter(task => {
    if (!task.due_date || task.status === 'completed') return false;
    const dueDate = new Date(task.due_date);
    return dueDate <= thisWeekEnd;
  }).length;

  // Calculate completed count
  const completedCount = tasks.filter(task => task.status === 'completed').length;
  const totalCount = tasks.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className={className}>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">
        Progress Summary
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Next Action Card */}
        {nextAction && (
          <SummaryCard
            icon={Target}
            title="Next Action"
            value={nextAction.subtask.title.length > 30
              ? nextAction.subtask.title.substring(0, 30) + '...'
              : nextAction.subtask.title
            }
            subtitle={
              nextAction.subtask.due_date
                ? `Due ${formatDueDate(nextAction.subtask.due_date)}`
                : 'Start now'
            }
            color="indigo"
            onClick={onNextActionClick}
          />
        )}

        {/* In Progress Card */}
        <SummaryCard
          icon={Loader}
          title="In Progress"
          value={inProgressCount}
          subtitle={inProgressCount === 1 ? 'active task' : 'active tasks'}
          color="purple"
          onClick={onInProgressClick}
        />

        {/* This Week Card */}
        <SummaryCard
          icon={Calendar}
          title="Due This Week"
          value={thisWeekCount}
          subtitle={thisWeekCount === 1 ? 'upcoming deadline' : 'upcoming deadlines'}
          color="orange"
          onClick={onThisWeekClick}
        />

        {/* Overall Progress Card */}
        <SummaryCard
          icon={CheckCircle2}
          title="Overall Progress"
          value={`${completionPercentage}%`}
          subtitle={`${completedCount} of ${totalCount} completed`}
          color="green"
        />
      </div>
    </div>
  );
};

export default ProgressSummary;
